import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Search, Package, Truck, CheckCircle2, ShoppingBag, CreditCard, XCircle,
  MapPin, Clock, Copy, ExternalLink, ArrowLeft, ChevronRight, Shield,
  RotateCcw, Headphones, AlertCircle, Loader2
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  'shopping-bag': ShoppingBag,
  'credit-card': CreditCard,
  'package': Package,
  'truck': Truck,
  'check-circle': CheckCircle2,
  'x-circle': XCircle,
};

const STATUS_LABEL = {
  pending: 'Aguardando pagamento',
  pending_payment: 'Aguardando pagamento',
  paid: 'Pagamento confirmado',
  processing: 'Em preparação',
  shipped: 'Em transporte',
  delivered: 'Entregue',
  cancelled: 'Cancelado',
};

const STATUS_TONE = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  pending_payment: 'bg-amber-50 text-amber-700 border-amber-200',
  paid: 'bg-blue-50 text-blue-700 border-blue-200',
  processing: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  shipped: 'bg-violet-50 text-violet-700 border-violet-200',
  delivered: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  cancelled: 'bg-rose-50 text-rose-700 border-rose-200',
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('pt-BR', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  } catch { return ''; }
};

const formatBRL = (n) => `R$ ${Number(n || 0).toFixed(2).replace('.', ',')}`;

export default function TrackingPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [orderId, setOrderId] = useState(searchParams.get('pedido') || '');
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [phone, setPhone] = useState('');
  const [activeTab, setActiveTab] = useState('order'); // 'order' | 'contact'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [order, setOrder] = useState(null);
  const [orderList, setOrderList] = useState(null);

  // Auto-busca se vier com ?pedido=... na URL
  useEffect(() => {
    const auto = searchParams.get('pedido');
    if (auto && !order && !loading) {
      handleTrack({ preventDefault: () => {} }, auto, '', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTrack = async (e, ovOrderId, ovEmail, ovPhone) => {
    e?.preventDefault?.();
    setError('');
    setOrder(null);
    setOrderList(null);

    const payload = {};
    const oid = (ovOrderId ?? orderId).trim();
    const em = (ovEmail ?? email).trim();
    const ph = (ovPhone ?? phone).trim();

    if (activeTab === 'order') {
      if (!oid) { setError('Informe o número do pedido.'); return; }
      payload.order_id = oid;
    } else {
      if (!em || !ph) { setError('Informe email e telefone.'); return; }
      payload.email = em;
      payload.phone = ph;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/orders/track`, payload);
      if (res.data?.orders) {
        if (res.data.orders.length === 1) {
          setOrder(res.data.orders[0]);
        } else {
          setOrderList(res.data.orders);
        }
      } else {
        setOrder(res.data);
        // Atualiza URL para deep link
        if (payload.order_id) {
          setSearchParams({ pedido: payload.order_id }, { replace: true });
        }
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Não conseguimos localizar seu pedido. Verifique os dados e tente novamente.';
      setError(typeof msg === 'string' ? msg : 'Erro ao buscar pedido.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Código copiado!');
  };

  return (
    <div className="min-h-screen bg-[#f7f7f7]" data-testid="tracking-page">
      {/* Hero / Header */}
      <section className="bg-gradient-to-br from-[#0d0d0d] via-[#161616] to-[#0d0d0d] text-white border-b border-white/5">
        <div className="max-w-5xl mx-auto px-4 pt-10 pb-12 md:pt-16 md:pb-16">
          <Link to="/" className="inline-flex items-center text-sm text-white/60 hover:text-white mb-6 transition-colors" data-testid="back-home">
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Voltar ao início
          </Link>

          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div className="max-w-2xl">
              <p className="text-[#C4A35A] text-[11px] font-semibold uppercase tracking-[0.3em] mb-3">
                Central de Pedidos
              </p>
              <h1
                className="text-3xl md:text-5xl font-bold leading-tight mb-3"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Rastreie seu pedido
              </h1>
              <p className="text-white/70 text-sm md:text-base leading-relaxed">
                Acompanhe em tempo real cada etapa da sua compra — do pagamento confirmado
                até a entrega na sua porta. Insira abaixo o número do pedido ou seu email e telefone.
              </p>
            </div>
            <div className="hidden md:flex items-center gap-6 text-xs text-white/50">
              <div className="text-center">
                <Truck className="h-6 w-6 mx-auto mb-1 text-[#C4A35A]" />
                <span>Envio rápido</span>
              </div>
              <div className="text-center">
                <Shield className="h-6 w-6 mx-auto mb-1 text-emerald-400" />
                <span>Compra segura</span>
              </div>
              <div className="text-center">
                <RotateCcw className="h-6 w-6 mx-auto mb-1 text-blue-400" />
                <span>Troca grátis</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Form Card */}
      <section className="max-w-3xl mx-auto px-4 -mt-8 md:-mt-10 relative z-10">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden" data-testid="tracking-form-card">
          {/* Tabs */}
          <div className="flex border-b border-gray-100">
            <button
              type="button"
              onClick={() => { setActiveTab('order'); setError(''); }}
              className={`flex-1 py-4 px-4 text-sm font-semibold transition-colors ${
                activeTab === 'order'
                  ? 'text-[#0a0a0a] border-b-2 border-[#0a0a0a] bg-white'
                  : 'text-gray-500 hover:text-gray-800 bg-gray-50'
              }`}
              data-testid="tab-order"
            >
              <span className="flex items-center justify-center gap-2">
                <Package className="h-4 w-4" /> Por número do pedido
              </span>
            </button>
            <button
              type="button"
              onClick={() => { setActiveTab('contact'); setError(''); }}
              className={`flex-1 py-4 px-4 text-sm font-semibold transition-colors ${
                activeTab === 'contact'
                  ? 'text-[#0a0a0a] border-b-2 border-[#0a0a0a] bg-white'
                  : 'text-gray-500 hover:text-gray-800 bg-gray-50'
              }`}
              data-testid="tab-contact"
            >
              <span className="flex items-center justify-center gap-2">
                <Headphones className="h-4 w-4" /> Por email + telefone
              </span>
            </button>
          </div>

          <form onSubmit={handleTrack} className="p-5 md:p-7 space-y-4">
            {activeTab === 'order' ? (
              <div>
                <label className="text-[11px] font-semibold text-gray-700 uppercase tracking-wider mb-2 block">
                  Número do pedido
                </label>
                <div className="relative">
                  <Input
                    type="text"
                    value={orderId}
                    onChange={(e) => setOrderId(e.target.value)}
                    placeholder="Ex: ord_a1b2c3d4 (vem no seu email de confirmação)"
                    className="pl-11 h-12 rounded-xl border-gray-300 focus:border-[#0a0a0a]"
                    data-testid="input-order-id"
                  />
                  <Package className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                </div>
                <p className="text-[11px] text-gray-500 mt-2 flex items-start gap-1.5">
                  <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                  Você recebeu o número do pedido no email de confirmação.
                </p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-[11px] font-semibold text-gray-700 uppercase tracking-wider mb-2 block">
                    Email
                  </label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="seu@email.com"
                    className="h-12 rounded-xl border-gray-300"
                    data-testid="input-email"
                  />
                </div>
                <div>
                  <label className="text-[11px] font-semibold text-gray-700 uppercase tracking-wider mb-2 block">
                    Telefone (com DDD)
                  </label>
                  <Input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="(11) 99999-9999"
                    className="h-12 rounded-xl border-gray-300"
                    data-testid="input-phone"
                  />
                </div>
              </div>
            )}

            {error && (
              <div className="flex items-start gap-2 p-3 bg-rose-50 border border-rose-200 rounded-xl text-rose-700 text-sm" data-testid="tracking-error">
                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-xl bg-[#0a0a0a] hover:bg-[#1f1f1f] text-white font-semibold text-base"
              data-testid="btn-track"
            >
              {loading ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Buscando seu pedido...</>
              ) : (
                <><Search className="h-4 w-4 mr-2" /> Rastrear meu pedido</>
              )}
            </Button>
          </form>
        </div>
      </section>

      {/* RESULT - lista de pedidos */}
      {orderList && (
        <section className="max-w-3xl mx-auto px-4 mt-8">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">
            Encontramos {orderList.length} pedido(s)
          </h2>
          <div className="space-y-3">
            {orderList.map((o) => (
              <button
                key={o.order_id}
                onClick={() => { setOrder(o); setOrderList(null); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className="w-full bg-white rounded-xl border border-gray-200 p-4 text-left hover:border-[#0a0a0a] transition-colors flex items-center justify-between"
                data-testid={`order-list-item-${o.order_id}`}
              >
                <div>
                  <p className="text-xs text-gray-500">Pedido</p>
                  <p className="font-semibold text-gray-900 font-mono text-sm">{o.order_id}</p>
                  <p className="text-xs text-gray-500 mt-1">{formatDate(o.created_at)}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-semibold px-3 py-1 rounded-full border ${STATUS_TONE[o.status] || STATUS_TONE.pending}`}>
                    {STATUS_LABEL[o.status] || o.status}
                  </span>
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* RESULT - detalhe do pedido */}
      {order && (
        <section className="max-w-4xl mx-auto px-4 mt-8 pb-12" data-testid="tracking-result">
          {/* Resumo header */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 md:p-7 mb-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <p className="text-[11px] text-gray-500 uppercase tracking-wider mb-1">Pedido</p>
                <p className="font-mono font-bold text-lg text-gray-900" data-testid="order-id-display">{order.order_id}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Realizado em {formatDate(order.created_at)}
                </p>
              </div>
              <div className="flex flex-col md:items-end gap-2">
                <span className={`inline-block text-xs font-semibold px-3 py-1.5 rounded-full border ${STATUS_TONE[order.status] || STATUS_TONE.pending}`} data-testid="order-status-badge">
                  {STATUS_LABEL[order.status] || order.status}
                </span>
                <p className="text-2xl font-bold text-[#0a0a0a]">{formatBRL(order.total)}</p>
              </div>
            </div>
          </div>

          {/* Tracking code highlight */}
          {order.tracking_code && (
            <div className="bg-gradient-to-br from-violet-50 to-indigo-50 rounded-2xl border border-violet-200 p-5 md:p-6 mb-6" data-testid="tracking-code-box">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-violet-600 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Truck className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-[11px] text-violet-700 font-semibold uppercase tracking-wider mb-1">
                    Código de rastreio {order.carrier ? `· ${order.carrier}` : ''}
                  </p>
                  <p className="text-xl md:text-2xl font-mono font-bold text-gray-900 break-all">
                    {order.tracking_code}
                  </p>
                  <div className="flex flex-wrap items-center gap-2 mt-3">
                    <button
                      onClick={() => handleCopy(order.tracking_code)}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 bg-white rounded-full border border-violet-200 hover:bg-violet-50 text-violet-700"
                      data-testid="btn-copy-tracking"
                    >
                      <Copy className="h-3.5 w-3.5" /> Copiar
                    </button>
                    {order.tracking_url && (
                      <a
                        href={order.tracking_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 bg-violet-600 hover:bg-violet-700 text-white rounded-full"
                        data-testid="btn-tracking-url"
                      >
                        <ExternalLink className="h-3.5 w-3.5" /> Rastrear na transportadora
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Timeline */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 md:p-7 mb-6" data-testid="tracking-timeline">
            <h3 className="text-base font-bold text-gray-900 mb-5 flex items-center gap-2">
              <Clock className="h-5 w-5 text-gray-400" /> Status do envio
            </h3>
            <ol className="relative space-y-6">
              {(order.timeline || []).map((step, idx, arr) => {
                const Icon = ICON_MAP[step.icon] || Package;
                const isLast = idx === arr.length - 1;
                const dotClass = step.completed
                  ? (step.key === 'cancelled' ? 'bg-rose-500 ring-rose-100' : 'bg-emerald-500 ring-emerald-100')
                  : step.active
                  ? 'bg-[#0a0a0a] ring-gray-200 animate-pulse'
                  : 'bg-gray-200 ring-gray-100';
                const lineClass = step.completed ? 'bg-emerald-500' : 'bg-gray-200';
                const textClass = step.completed || step.active ? 'text-gray-900' : 'text-gray-400';
                return (
                  <li key={step.key} className="relative flex gap-4" data-testid={`timeline-step-${step.key}`}>
                    {/* Line */}
                    {!isLast && (
                      <span className={`absolute left-[18px] top-10 w-0.5 h-full -ml-px ${lineClass}`} aria-hidden="true" />
                    )}
                    {/* Dot */}
                    <div className={`relative z-10 w-9 h-9 rounded-full ring-4 ${dotClass} flex items-center justify-center flex-shrink-0`}>
                      <Icon className="h-4 w-4 text-white" />
                    </div>
                    {/* Content */}
                    <div className="flex-1 pt-1">
                      <p className={`font-semibold text-sm ${textClass}`}>{step.label}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{step.description}</p>
                      {step.date && (
                        <p className="text-[11px] text-gray-400 mt-1">{formatDate(step.date)}</p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>

          {/* Detalhes do pedido */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Items */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                <ShoppingBag className="h-4 w-4 text-gray-400" /> Itens do pedido
              </h3>
              <div className="space-y-2">
                {(order.items || []).map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3 py-2 border-b border-gray-50 last:border-0">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {item.name || item.product_name || `Produto ${item.product_id || ''}`}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {item.size && `Tam ${item.size}`}
                        {item.color && ` · ${item.color}`}
                        {` · ${item.quantity || 1}x`}
                      </p>
                    </div>
                    {item.price != null && (
                      <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                        {formatBRL((item.price || 0) * (item.quantity || 1))}
                      </p>
                    )}
                  </div>
                ))}
              </div>
              {/* Resumo */}
              <div className="mt-4 pt-4 border-t border-gray-100 space-y-1 text-sm">
                {order.subtotal != null && (
                  <div className="flex justify-between text-gray-600">
                    <span>Subtotal</span><span>{formatBRL(order.subtotal)}</span>
                  </div>
                )}
                {order.coupon_code && order.coupon_discount > 0 && (
                  <div className="flex justify-between text-emerald-700">
                    <span>Cupom {order.coupon_code}</span><span>-{formatBRL(order.coupon_discount)}</span>
                  </div>
                )}
                {order.cashback_used > 0 && (
                  <div className="flex justify-between text-emerald-700">
                    <span>Cashback usado</span><span>-{formatBRL(order.cashback_used)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-gray-900 text-base pt-2 border-t border-gray-100 mt-2">
                  <span>Total</span><span>{formatBRL(order.total)}</span>
                </div>
              </div>
            </div>

            {/* Entrega */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                <MapPin className="h-4 w-4 text-gray-400" /> Entrega
              </h3>
              <div className="space-y-2 text-sm">
                {order.customer_name && (
                  <p className="text-gray-600">
                    Para <span className="font-medium text-gray-900">{order.customer_name}</span>
                  </p>
                )}
                {(order.address_city || order.address_state) && (
                  <p className="text-gray-600">
                    {order.address_city ? `${order.address_city}/` : ''}{order.address_state || ''}
                  </p>
                )}
                {order.address_cep && (
                  <p className="text-gray-500 text-xs">CEP {order.address_cep}</p>
                )}
                {order.payment_method && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <p className="text-xs text-gray-500">Pagamento</p>
                    <p className="text-sm font-medium text-gray-900 capitalize">{order.payment_method}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Help footer */}
          <div className="mt-6 bg-emerald-50 border border-emerald-200 rounded-2xl p-5 flex items-start gap-4">
            <Headphones className="h-6 w-6 text-emerald-700 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-semibold text-emerald-900 text-sm">Precisa de ajuda com seu pedido?</p>
              <p className="text-xs text-emerald-800 mt-0.5">
                Nosso atendimento está disponível pelo WhatsApp para tirar suas dúvidas e acompanhar a entrega.
              </p>
            </div>
            <a
              href="https://wa.me/5562994692718?text=Ol%C3%A1!%20Preciso%20de%20ajuda%20com%20meu%20pedido."
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs font-semibold px-4 py-2.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-full whitespace-nowrap"
              data-testid="tracking-help-whatsapp"
            >
              Falar com atendimento
            </a>
          </div>
        </section>
      )}

      {/* SEM RESULTADO - guias informativos */}
      {!order && !orderList && (
        <section className="max-w-5xl mx-auto px-4 mt-10 pb-12">
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-white rounded-2xl border border-gray-100 p-5">
              <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center mb-3">
                <span className="text-blue-700 font-bold">1</span>
              </div>
              <h4 className="font-semibold text-gray-900 mb-1 text-sm">Confirme seu pedido</h4>
              <p className="text-xs text-gray-600 leading-relaxed">
                Após a confirmação do pagamento, você recebe um email com o número do pedido.
              </p>
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 p-5">
              <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center mb-3">
                <span className="text-indigo-700 font-bold">2</span>
              </div>
              <h4 className="font-semibold text-gray-900 mb-1 text-sm">Preparo e envio</h4>
              <p className="text-xs text-gray-600 leading-relaxed">
                Em até 24h preparamos e enviamos. Você recebe o código de rastreio aqui mesmo nesta página.
              </p>
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 p-5">
              <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center mb-3">
                <span className="text-emerald-700 font-bold">3</span>
              </div>
              <h4 className="font-semibold text-gray-900 mb-1 text-sm">Acompanhe a entrega</h4>
              <p className="text-xs text-gray-600 leading-relaxed">
                Volte aqui a qualquer momento para ver o status atualizado do seu pedido em tempo real.
              </p>
            </div>
          </div>

          {/* FAQ rápido */}
          <div className="mt-8 bg-white rounded-2xl border border-gray-100 p-5 md:p-7">
            <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-gray-400" /> Perguntas frequentes
            </h3>
            <div className="space-y-3 text-sm">
              <details className="group bg-gray-50 rounded-xl p-4 cursor-pointer">
                <summary className="font-semibold text-gray-900 list-none flex justify-between items-center">
                  <span>Quanto tempo leva para chegar?</span>
                  <ChevronRight className="h-4 w-4 text-gray-400 group-open:rotate-90 transition-transform" />
                </summary>
                <p className="text-gray-600 mt-2 leading-relaxed">
                  Enviamos em até 24h após a confirmação do pagamento. O prazo de entrega varia conforme sua região e a transportadora — geralmente entre 3 e 10 dias úteis.
                </p>
              </details>
              <details className="group bg-gray-50 rounded-xl p-4 cursor-pointer">
                <summary className="font-semibold text-gray-900 list-none flex justify-between items-center">
                  <span>Não recebi o número do pedido</span>
                  <ChevronRight className="h-4 w-4 text-gray-400 group-open:rotate-90 transition-transform" />
                </summary>
                <p className="text-gray-600 mt-2 leading-relaxed">
                  Verifique seu email (inclusive a caixa de spam). Você também pode rastrear pelo seu email + telefone usando a aba acima.
                </p>
              </details>
              <details className="group bg-gray-50 rounded-xl p-4 cursor-pointer">
                <summary className="font-semibold text-gray-900 list-none flex justify-between items-center">
                  <span>Posso trocar meu pedido?</span>
                  <ChevronRight className="h-4 w-4 text-gray-400 group-open:rotate-90 transition-transform" />
                </summary>
                <p className="text-gray-600 mt-2 leading-relaxed">
                  Sim! Troca grátis em até 7 dias após a entrega. Entre em contato pelo WhatsApp que processamos para você.
                </p>
              </details>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
